import { Component, EventEmitter, forwardRef, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DateHelper } from 'app/main/content/misc/date.module';

@Component({
  selector: 'app-intec-date-picker',
  templateUrl: './intec-date-picker.component.html',
  styleUrls: ['./intec-date-picker.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IntecDatePickerComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => IntecDatePickerComponent), multi: true },
  ],
})
export class IntecDatePickerComponent implements ControlValueAccessor, OnDestroy {

  // references
  // https://medium.com/@tarik.nzl/angular-2-custom-form-control-with-validation-json-input-2b4cf9bc2d73
  // https://alligator.io/angular/custom-form-control/

  // flags for custom validation errors
  invalidDateError = false;
  parseError = false;
  checkForParseError = true;

  // backing variable for value.  This HAS to be a date value or the binding to calendar won't work
  _value: Date;

  startAt: any;

  // debouncer used to debounce the typed input
  debouncer: Subject<any> = new Subject();

  placeHolderString : string="";
  @Input() set placeholder(val:string)
  {
      this.placeHolderString=val;
      this.showClearButton =(this.placeHolderString=='Default Date' || this.showClearButton);
  }
  get placeholder()
  {
    return this.placeHolderString;
  }


  @Input() min: any;
  @Input() max: any;
  @Input() required = false;
  @Input() readonly: boolean;
  @Input() allowRelativeDate = false;
  @Input() showClearButton=false;

  @Input() set value(val: any) {
    this.writeValue(val);
  }
  get value(): any {
    return this._value;
  }

  @Output() dateChange = new EventEmitter();

  @ViewChild('dateInputControl') dateInput;

  constructor(private dateHelper: DateHelper) {

    // setup debouncer for the typed input
    this.debouncer
      .pipe(debounceTime(500))
      .subscribe((val) => {
        this.setAndNotify(val, true);
      });

    // set the starting date for the calendar picker to the current date
    this.startAt = new Date();
    this.showClearButton==(this.placeholder=="Default Date") || this.showClearButton;
  }

   ngOnDestroy(): void {
     if (this.debouncer) this.debouncer.unsubscribe();
   }

  // ** start of items required for the control value accessor **
  @Input() disabled = false;

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onTouched = () => { };

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private propagateChange = (_: any) => { };

  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  writeValue(obj: any) {
    if (obj) {
      this.setDate(obj, false);
    }
    else {
      this._value = undefined;
    }
  }
  // ** end of items required for the control value accessor **

  // set value and emit via valueChanged output
  private setAndNotify(val: any, enableParseCheck: boolean) {
    this.checkForParseError = enableParseCheck;
    const d = this.setDate(val, true);
    this.valueChanged(d ? this.dateHelper.getDateAsString(d) : null);
  }

  private setDate(val: any, propagate: boolean): Date {
    let d = null;    

    if (val==null)
    {
      this.parseError = false;
      this.invalidDateError = false;
      this._value=null;
      this.validate(this.dateInput?.control);
      this.dateChange.next(this._value);

      this.dateInput?.control?.markAsDirty();
      if (propagate) {
        setTimeout(() => {this.propagateChange(null);}, 200);
      }
      return;
    }

    // check if relative data allowed and if has relative date
    if (this.allowRelativeDate || (!this.dateHelper.isRelativeDate(val))) {

      // relative date allowed, or not relative date - get relevant value as date
      d = this.dateHelper.parse(val) || this.dateHelper.parse(this._value);
      if (d) {
        if (this.dateHelper.isValidDate(d)) {
          this.parseError = this.checkForParseError && (d.getSeconds() === 1);
          this.invalidDateError = (d.getSeconds() === 2);
        }
        else {
          this.invalidDateError = true;
        }
      }
      else {
        this.parseError = false;
        this.invalidDateError = false;
      }
      // set backing variable to required value if no errors
      if (!(this.parseError || this.invalidDateError)) {
        this._value = d;
      }

      // set calendar starting date to new value or current date if new value greater than the today calc threshold
      this.startAt = (this._value && (!this.dateHelper.isRelativeDate(this.dateHelper.utc(d)))) ? this._value : new Date();
    }
    else {
      // relative date not allowed, set invalid date error flag
      this.invalidDateError = true;
    }   
    
    // PJT - Make sure control have correct value from above    
    if (this.dateInput && this.dateInput.control && this._value)
      this.dateInput.control.value=this.dropTimeFromDate(this._value);

    // Force Validate
    this.validate(this.dateInput?.control);

    // If we have a value then remove any validation error 'required' as it is not true.
    if (this._value && this.dateInput?.control && this.dateInput?.control?.errors)  
      delete this.dateInput.control.errors['required'];
  
    this.dateChange.next(this._value);

    // propagate the change - we need to do this so the validation is processed
    if (propagate) {
      setTimeout(() => {this.propagateChange(d);}, 200);
    }
    return d;
  }

  dropTimeFromDate(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }

  private getInputValue() {
    if (this.dateInput) {
      const e = (this.dateInput.control.errors && this.dateInput.control.errors['matDatepickerParse']) ? this.dateInput.control.errors['matDatepickerParse'].text : null;
      const v = (this.dateInput.model) ? this.dateInput.value : null;
      return e || v;
    }
    else
      return null;
  }

  public validate(c: UntypedFormControl) {    
    if (this.dateInput){
      const v = this.getInputValue();
      const errors = this.dateInput.errors || {};
      const value = v || c.value;
      const hasValue = ((value) && (value !== '0')) ? true : false;
      if ((!hasValue) && (this.dateInput.errors)){
        // there is a bug which sets the matDatepickerParse error if the value is '0' or empty string
        // in theory we should never hit this line but added just in case as a quick hack around the problem :).
        delete errors['matDatepickerParse'];
      }
      if (this.parseError){ errors['dateParseError'] = true; }
      if (this.invalidDateError){ errors['invalidDate'] = true; }
      return errors;
    }
  }

  calendarInputChange(event) {
    this.setAndNotify(event.targetElement.value, false);
  }

  // make sure that the date input is in the right format, and a valid string
  dateInputChange(event) {
    if (event){
      const val = event ? event.targetElement.value : ((this.dateInput) ? this.getInputValue() : null);
      if(val=="")
        this.setAndNotify(null,true);
      else 
        // use debouncer to set value
        this.debouncer.next(val);
    }
  }

  valueChanged(val) {
    this.dateChange.emit(val);
  }

  clearStartDate()
  {        
    this.setAndNotify(null, true);
  }
}
